import {ComponentFactoryResolver, ComponentRef, Injectable, Injector, ViewContainerRef} from '@angular/core';
import {IssuecardComponent} from './issuecard.component';
import {DynamicElementService} from '../../services/dynamicElement.service';
import {InteractionService} from '../../services/interaction.service';
import {SynchronizationService} from '../../services/synchronization.service';
import {HTMLElementPositioningHelper} from '../../util/htmlelement-positioning-helper';
import {Issue} from '../../models/issue';
import {Status} from '../../models/status';
import {UserstoryService} from '../../services/userstory.service';
import {Userstory} from '../../models/userstory';
import interact from 'interactjs';
import {Task} from '../../models/task';
import {EventIssuecardToForeground} from '../../models/syncEvents/realtimeEvents/eventIssuecardToForeground';
import {EventIssuecardMoved} from '../../models/syncEvents/realtimeEvents/eventIssuecardMoved';
import {IssueSynchroHelper} from '../issue/issue.synchro.helper';
import {EventIssuecardOpened} from '../../models/syncEvents/realtimeEvents/eventIssuecardOpened';
import {OnlineuserService} from '../../services/onlineuser.service';
import {EventIssueToForeground} from '../../models/syncEvents/persistenceEvents/eventIssueToForeground';

/**
 * Helper class encapsulating dialog-related tasks for issue.component
 */
@Injectable()
export class IssuecardHelper {

  public constructor(
    private interactionService: InteractionService,
    private syncService: SynchronizationService,
    private resolver: ComponentFactoryResolver,
    private positionHelper: HTMLElementPositioningHelper,
    private dynamicElementService: DynamicElementService,
    private userstoryService: UserstoryService,
    private onlineUserService: OnlineuserService
  ) { }


  /**
   * Open a dialog (IssueCard) for the creation of a new Issue
   *
   * @param projectKey
   * @param statuses
   * @param onCloseCallback
   * @param onNewIssueCreatedCallback
   * @param title
   * @param mouseEvent use to set the position of the new card to the mouseevents coordinates
   * @param syncEvent
   */
  openNewIssueDialog(
    projectKey: string,
    statuses: Status[],
    onCloseCallback,
    onNewIssueCreatedCallback,
    mouseEvent?: MouseEvent,
    syncEvent?: EventIssuecardOpened,
    title?: string): void {
    const statusTodo: Status = IssuecardHelper.getStatusForNewIssue(statuses);
    // If we react to a sync event we use the new-issueId of the event, else we create one
    const newIssueId: number = (syncEvent) ? syncEvent.issueId : this.userstoryService.getTemporaryIdForNewIssue();
    const newIssue: Userstory = new Userstory(null, '', undefined, newIssueId, null, null, title || '', 1, null, statusTodo,
      [], [], null, null, null, false, '', '', '', []);
    const card: IssuecardComponent = this.createCard(
      newIssue, projectKey, statuses, onCloseCallback, onNewIssueCreatedCallback, null, null, null, null,
      (syncEvent) ? syncEvent.avatar : ''
    );
    this.positionAndFocusNewCard(card, mouseEvent, syncEvent);

    // Only send sync-Event if we are not reacting to a sync event ourselves (else we create a loop)
    if (!syncEvent) {
      IssueSynchroHelper.sendIssueCardOpenedEvent(this.syncService, card, this.positionHelper, this.onlineUserService);
    }
  }

  /**
   * Open a dialog (IssueCard) for the creation of a new Subissue
   *
   * @param projectKey
   * @param statuses
   * @param onCloseCallback
   * @param onNewSubissueCreatedCallback
   * @param parentIssueId
   * @param mouseEvent use to set the position of the new card to the mouseevents coordinates
   * @param syncEvent
   */
  openNewSubissueDialog(
    projectKey: string,
    statuses: Status[],
    onCloseCallback,
    onNewSubissueCreatedCallback,
    parentIssueId: number,
    mouseEvent?: MouseEvent,
    syncEvent?: EventIssuecardOpened): void {

    const statusTodo: Status = IssuecardHelper.getStatusForNewIssue(statuses);
    // If we react to a sync event we use the new-issueId of the event, else we create one
    const newIssueId: number = (syncEvent) ? syncEvent.issueId : this.userstoryService.getTemporaryIdForNewIssue();
    const newSubissue: Task = new Task(newIssueId, '', '', 0, 0, null, null, statusTodo, parentIssueId, [], [], '', false, [], '', '', '');

    const card: IssuecardComponent = this.createCard(
      newSubissue, projectKey, statuses, onCloseCallback, null, onNewSubissueCreatedCallback, null, null, null,
      (syncEvent) ? syncEvent.avatar : '');
    this.positionAndFocusNewCard(card, mouseEvent, syncEvent);

    // Only send sync-Event if we are not reacting to a sync event ourselves (else we create a loop)
    if (!syncEvent) {
      IssueSynchroHelper.sendIssueCardOpenedEvent(this.syncService, card, this.positionHelper, this.onlineUserService);
    }
  }

  /**
   * @param card
   * @param mouseEvent use to set the position of the new card according to mouseevent coordinates
   * @param syncEvent
   */
  private positionAndFocusNewCard(card: IssuecardComponent, mouseEvent?: MouseEvent, syncEvent?: EventIssuecardOpened): void {
    const cardElement = card.getElementRef().nativeElement;

    if (mouseEvent) {
      const pos = this.positionHelper.getPositionForNewCard(mouseEvent, cardElement);
      this.positionHelper.setAbsolutePosition(cardElement, pos.x, pos.y);
    }
    if (syncEvent && !mouseEvent) {
      this.positionHelper.setRelativePosition(cardElement, syncEvent.position.left, syncEvent.position.top);
    }

    // Set focus on the title-input-field of the card
    cardElement.querySelector('input#dialog-title').focus();
  }

  /**
   * @param issue the issue to be displayed in the issuecard
   * @param projectKey
   * @param statuses the statuses for the current board
   * @param onCloseCallback callback that should be called when the card is closed
   * @param onNewIssueCreatedCallback callback that should be called when a new issue is created by the card
   * @param onNewSubIssueCreatedCallback
   * @param onIssueRemovedCallback
   * @param onSubIssueRemovedCallback
   * @param onSubIssueModifiedCallback callback that should be called when the issue is modified/updated
   * @param avatar
   */
  private createCard(issue: Issue, projectKey: string, statuses: Status[] | Status,
    onCloseCallback, onNewIssueCreatedCallback, onNewSubIssueCreatedCallback, onIssueRemovedCallback,
    onSubIssueRemovedCallback, onSubIssueModifiedCallback, avatar: string): IssuecardComponent {
    console.log(avatar);
    const injector = Injector.create({
      providers: [
        { provide: 'issue', useValue: issue },
        { provide: 'projectKey', useValue: projectKey },
        { provide: 'statuses', useValue: statuses },
        { provide: 'onCloseCallback', useValue: (issuecard) => onCloseCallback(issuecard) },
        { provide: 'onNewIssueCreatedCallback', useValue: (createdIssue) => onNewIssueCreatedCallback(createdIssue) },
        { provide: 'onNewSubIssueCreatedCallback', useValue: (createdIssue) => onNewSubIssueCreatedCallback(createdIssue) },
        { provide: 'onIssueRemovedCallback', useValue: (removedIssue) => onIssueRemovedCallback(removedIssue) },
        { provide: 'onSubIssueRemovedCallback', useValue: (removedIssue) => onSubIssueRemovedCallback(removedIssue) },
        { provide: 'onIssueModified', useValue: (modifiedIssues) => onSubIssueModifiedCallback(modifiedIssues) },
        { provide: 'avatar', useValue: avatar}
      ]
    });
    const insertionPoint: ViewContainerRef = this.dynamicElementService.getInsertionPoint();
    const componentRef: ComponentRef<IssuecardComponent> = insertionPoint.createComponent(IssuecardComponent, { injector: injector });
    const card: IssuecardComponent = componentRef.instance;
    card.componentRef = componentRef;

    this.setupIssueCardAsDraggable(card);
    this.setupIssueCardAsClickable(card);
    this.dynamicElementService.insert(componentRef);

    // Update last viewed timestamp when card is created/opened
    this.userstoryService.setLastViewed(projectKey, issue.id).subscribe();
    issue.lastViewedInAwall = new Date().toISOString();

    return card;
  }

  setupIssueCardAsDraggable(issuecard: IssuecardComponent): void {
    const cardElement: HTMLElement = issuecard.getElementRef().nativeElement;
    const cardHeaderElement: HTMLElement = issuecard.getElementRef().nativeElement.querySelector('div.dialog__header');
    this.interactionService.setUpDraggable(cardHeaderElement, cardElement, this.getOnIssueCardMovedSyncFunction(issuecard));
  }

  setupIssueCardAsClickable(issuecard: IssuecardComponent): void {
    this.interactionService.addClickInteractionElement(issuecard.getElementRef(), this.getOnIssueCardClickedSyncFunction(issuecard));
  }

  /**
   * Removes the card from dynamicElementService and unregisters global interactjs-listeners on the card.
   * Note that you might still have to do some removal-actions on a local (board-view) level.
   */
  removeCardFromGlobalReferences(issuecard: IssuecardComponent): void {
    // Remove interact listeners from card (draggable)
    // Since update to interactjs 1.4.0 this can throw an error, see:
    // https://www.bountysource.com/issues/73500059-cannot-set-property-activedrops-of-undefined
    // Waiting for new interactjs version to fix this
    try {
      interact(issuecard.componentRef.getElementRef().nativeElement).unset();
    } catch {
      // Nothing to do
    }
    this.dynamicElementService.remove(issuecard.componentRef);
  }

  private getOnIssueCardMovedSyncFunction(issuecard: IssuecardComponent): any {
    return  (event: any): void => {
      this.dynamicElementService.select(issuecard.componentRef);

      const issueId = event.target.getAttribute('data-issueid');
      const position = this.positionHelper.getRelativePositionOfElement(issuecard.getElementRef().nativeElement);
      this.syncService.sendRealTimeEventNew(new EventIssuecardMoved(issueId, position, this.onlineUserService.getAvatarUrl()));
      this.syncService.sendPersistenceEventNew(new EventIssueToForeground(issuecard.issue.id, this.onlineUserService.getAvatarUrl()));
    };
  }

  private getOnIssueCardClickedSyncFunction(issuecard: IssuecardComponent): any {
    return (): void => {
      this.dynamicElementService.select(issuecard.componentRef);
      this.syncService.sendRealTimeEventNew(new EventIssuecardToForeground(issuecard.issue.id, this.onlineUserService.getAvatarUrl()));
      this.syncService.sendPersistenceEventNew(new EventIssueToForeground(issuecard.issue.id, this.onlineUserService.getAvatarUrl()));
    };
  }
  /**
 * This returns the first Status hat has a statuscategory with key 'new.
 * This status should be used for new issues where no explizit status is set.
 */
  private static getStatusForNewIssue(statuses: Status[]): Status {
    return statuses.find(status => status.statusCategory.key === 'new' || status.statusCategory.key === 'open');
  }


}
